import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  // NOTICE: KAPTION-INTEGRATION
  // Pagination and sorting state
  pagination: {
    currentPage: 1,
    totalPages: 0,
    perPage: 10,
    totalCount: 0,
  },
  sorting: {
    sortBy: 'created_at',
    sortOrder: 'desc',
  },
  // NOTICE: KAPTION-INTEGRATION
  // Archive filter: 'active' (default), 'archived', 'all'
  archiveFilter: 'active',
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isArchiving: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  // NOTICE: KAPTION-INTEGRATION
  // Get pagination state
  getPagination: _state => _state.pagination,
  // Get sorting state
  getSorting: _state => _state.sorting,
  // NOTICE: KAPTION-INTEGRATION
  // Get archive filter state
  getArchiveFilter: _state => _state.archiveFilter,
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      // NOTICE: KAPTION-INTEGRATION
      // Apply sorting from state
      const { sortBy, sortOrder } = _state.sorting;
      return filteredRecords.sort((a1, a2) => {
        let value1;
        let value2;

        switch (sortBy) {
          case 'title':
            value1 = a1.title || '';
            value2 = a2.title || '';
            break;
          case 'campaign_status':
            value1 = a1.campaign_status || '';
            value2 = a2.campaign_status || '';
            break;
          case 'updated_at':
            value1 = new Date(a1.updated_at || 0);
            value2 = new Date(a2.updated_at || 0);
            break;
          case 'created_at':
          default:
            value1 = new Date(a1.created_at || 0);
            value2 = new Date(a2.created_at || 0);
            break;
        }

        if (sortOrder === 'asc') {
          if (value1 < value2) return -1;
          if (value1 > value2) return 1;
          return 0;
        }
        if (value1 > value2) return -1;
        if (value1 < value2) return 1;
        return 0;
      });
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: (_state, _getters) => {
    const whatsappChannelTypes = [INBOX_TYPES.WHATSAPP];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, whatsappChannelTypes);
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatChannelTypes);
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

export const actions = {
  get: async function getCampaigns({ commit, state: _state }, options = {}) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      // NOTICE: KAPTION-INTEGRATION
      // Pass pagination, sorting, and archive filter options to API
      const params = {
        page: options.page || _state.pagination.currentPage,
        per_page: options.perPage || _state.pagination.perPage,
        sort_by: options.sortBy || _state.sorting.sortBy,
        sort_order: options.sortOrder || _state.sorting.sortOrder,
      };

      // NOTICE: KAPTION-INTEGRATION - Archive filter params
      const archiveFilter = options.archiveFilter || _state.archiveFilter;
      if (archiveFilter === 'archived') {
        params.archived_only = true;
      } else if (archiveFilter === 'all') {
        params.include_archived = true;
      }

      const response = await CampaignsAPI.get(params);

      // NOTICE: KAPTION-INTEGRATION
      // Handle both paginated and non-paginated responses
      // Paginated: { data: { meta: {...}, data: [...] } }
      // Non-paginated: { data: [...] }
      let campaigns;
      let meta;

      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        // Paginated response structure
        campaigns = response.data.data;
        meta = response.data.meta;
      } else if (Array.isArray(response.data)) {
        // Non-paginated response (backward compatibility)
        campaigns = response.data;
        meta = response.meta;
      } else {
        campaigns = response.data || [];
        meta = response.meta;
      }

      // Update records and pagination
      commit(types.SET_CAMPAIGNS, campaigns);
      if (meta) {
        commit(types.SET_CAMPAIGN_PAGINATION, {
          currentPage: meta.current_page,
          totalPages: meta.total_pages,
          perPage: meta.per_page,
          totalCount: meta.total_count,
        });
      }
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  // NOTICE: KAPTION-INTEGRATION
  // Archive a campaign
  archive: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isArchiving: true });
    try {
      await CampaignsAPI.archive(id);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isArchiving: false });
    }
  },
  // NOTICE: KAPTION-INTEGRATION
  // Unarchive a campaign
  unarchive: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isArchiving: true });
    try {
      await CampaignsAPI.unarchive(id);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isArchiving: false });
    }
  },
  // NOTICE: KAPTION-INTEGRATION
  // Set archive filter
  setArchiveFilter: ({ commit }, filter) => {
    commit(types.SET_CAMPAIGN_ARCHIVE_FILTER, filter);
  },
  // NOTICE: KAPTION-INTEGRATION
  // Update pagination
  updatePagination: ({ commit }, pagination) => {
    commit(types.SET_CAMPAIGN_PAGINATION, pagination);
  },
  // Update sorting
  updateSorting: ({ commit }, sorting) => {
    commit(types.SET_CAMPAIGN_SORTING, sorting);
  },
  // Fetch campaigns with specific page, sorting, and archive filter
  fetchPage: async (
    { dispatch },
    { page, perPage, sortBy, sortOrder, archiveFilter }
  ) => {
    await dispatch('get', { page, perPage, sortBy, sortOrder, archiveFilter });
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  // NOTICE: KAPTION-INTEGRATION
  // Set pagination state
  [types.SET_CAMPAIGN_PAGINATION](_state, pagination) {
    _state.pagination = {
      ..._state.pagination,
      ...pagination,
    };
  },
  // Set sorting state
  [types.SET_CAMPAIGN_SORTING](_state, sorting) {
    _state.sorting = {
      ..._state.sorting,
      ...sorting,
    };
  },

  // NOTICE: KAPTION-INTEGRATION
  // Set archive filter state
  [types.SET_CAMPAIGN_ARCHIVE_FILTER](_state, filter) {
    _state.archiveFilter = filter;
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
